import React from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, NavLink } from 'react-router-dom'
import ClickOutside from 'react-click-outside'
import classNames from 'classnames'

import { getModalLocation } from '../../../lib/modal'
import { ACCOUNT_SCREENS, MODALS, STATIC_SCREENS } from '../../../constants'
import NavLinkWithQuery from '../../../components/shared/nav-link-with-query'
import styles from './navigation-account.css'
import { showPlanUpgradePopup } from '../../../actions'
import { useNavigationAccount } from '../hooks/use-navigation-account'
import { useProfilesEntitlement } from '../../../hooks/useProfilesEntitlement'
import { E2E_SELECTORS } from '../../../e2e-selectors'
import { TESTERS } from '../../../lib/tester-classnames'
import { getProfileIsDisabled } from '../../../utils/getProfileIsDisabled'

/**
 * Renders the account dropdown menu, providing links and actions related to the user's account.
 * - My List (with total items badge)
 * - My Rentals (with total items badge)
 * - My Account
 * - Manage Profiles
 * - Help
 * - Log Out
 * - Switch Profile
 * - Exit Kids (for child profiles)
 * - List of other profiles for quick switching (for non-kid profiles only), including disabled profiles that trigger upsell popup
 */
function NavigationAccount() {
  const location = useLocation()
  const dispatch = useDispatch()

  const {
    display,
    handleClickOutside,
    onAccountNavClick,
    onLogoutClick,
    onSelectProfile,
    myRentalsTotalItems,
    helpURLConfig,
    email,
    currentProfile,
    accountData
  } = useNavigationAccount()

  const selectedProfile = accountData?.account?.selectedProfile
  const profiles = accountData?.account?.profiles?.filter(
    (profile) => profile.id !== selectedProfile
  )

  const { isProfileAllowBasicUpsell, profileLimit } = useProfilesEntitlement(accountData)

  const outsideClick = display ? handleClickOutside : () => {}

  const showUpgradePopup = () => dispatch(showPlanUpgradePopup())

  const onProfileClick = (isProfileDisabled, profile) => {
    if (isProfileDisabled) {
      showUpgradePopup()
    } else {
      onAccountNavClick()
      onSelectProfile(profile)
    }
  }

  const isKid = currentProfile?.isKid

  return (
    <ClickOutside onClickOutside={outsideClick}>
      <nav
        className={classNames(styles.accountNavWrapper, {
          [styles.hidden]: !display
        })}
      >
        <div className={styles.buttonPlaceholder} />
        <ul className={styles.accountLinks}>
          <li>
            <NavLink
              exact
              to={STATIC_SCREENS.MY_LISTS.screenId}
              onClick={onAccountNavClick}
              className={styles.accountLink}
              data-e2e={isKid ? E2E_SELECTORS.MY_LIST_KIDS_ACCOUNT : E2E_SELECTORS.ADULT_AVATAR_MENU_MY_LISTS}
            >
              My Lists
            </NavLink>
          </li>
          <li>
            <NavLink
              exact
              to={STATIC_SCREENS.MY_RENTALS.screenId}
              onClick={onAccountNavClick}
              className={styles.accountLink}
              data-lbx-e2e={isKid ? E2E_SELECTORS.MY_RENTALS_KIDS_ACCOUNT : E2E_SELECTORS.ADULT_AVATAR_MENU_MY_RENTALS}
            >
              My Rentals
              {myRentalsTotalItems > 0 && <span className={styles.total}>{`(${myRentalsTotalItems})`}</span>}
            </NavLink>
          </li>

          {isKid ? (
            <li>
              <NavLinkWithQuery
                exact
                to={getModalLocation(location, MODALS.qsParams.profilesSwitch)}
                onClick={onAccountNavClick}
                className={styles.accountLink}
              >
                Exit Kids
              </NavLinkWithQuery>
            </li>
          ) : (
            <>
              <li>
                <NavLink
                  exact
                  to={ACCOUNT_SCREENS.MY_ACCOUNT.path}
                  onClick={onAccountNavClick}
                  className={styles.accountLink}
                  data-e2e={E2E_SELECTORS.MY_ACCOUNT_IN_ACCOUNT_MENU}
                >
                  My Account
                </NavLink>
              </li>
              <li>
                <NavLinkWithQuery
                  exact
                  to={getModalLocation(location, MODALS.qsParams.profilesManagement)}
                  onClick={onAccountNavClick}
                  className={styles.accountLink}
                  data-e2e={E2E_SELECTORS.MANAGE_PROFILE_IN_ACCOUNT_MENU}
                >
                  Manage profiles
                </NavLinkWithQuery>
              </li>
              <li>
                <a
                  href={helpURLConfig}
                  onClick={onAccountNavClick}
                  className={styles.accountLink}
                  data-e2e={E2E_SELECTORS.HELP_IN_ACCOUNT_MENU}
                >
                  Help
                </a>
              </li>
              <li>
                <div
                  className={styles.accountLink}
                  onClick={() => {
                    onAccountNavClick()
                    onLogoutClick(email)
                  }}
                  data-e2e={E2E_SELECTORS.LOGOUT_IN_ACCOUNT_MENU}
                >
                  Log out
                </div>
              </li>
              <li>
                <NavLinkWithQuery
                  exact
                  to={getModalLocation(location, MODALS.qsParams.profilesSwitch)}
                  onClick={onAccountNavClick}
                  className={classNames(styles.accountLink, styles.switchProfile)}
                >
                  Switch profile
                </NavLinkWithQuery>
              </li>
            </>
          )}
        </ul>
        {
          !isKid &&
          profiles && profiles.map((profile, index) => {
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            // The active profile is not been accounted, so profileLimit - 1
            const isProfileDisabled = getProfileIsDisabled(isProfileAllowBasicUpsell, profileLimit - 1, index)
            return (
              <div
                className={classNames(TESTERS.NAVIGATION.PROFILE, styles.profile)}
                key={profile.id}
                onClick={() => onProfileClick(isProfileDisabled, profile)}
              >
                <img alt="Avatar" src={isProfileDisabled ? profile.avatar.uriForDisabledProfile : profile.avatar.uri} className={styles.avatar} />
                <span className={styles.userName} data-lbx-e2e={profile.name}>{profile.name}</span>
              </div>
            )
          })
        }
        <div className={styles.menuBackground} />
      </nav>
    </ClickOutside>
  )
}

export default NavigationAccount
